import React, { useEffect, useState, useRef } from "react";
import { Card, Row, Col, Container, Popover, Overlay } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import Calendar from "react-calendar";
import 'react-calendar/dist/Calendar.css';
import Layout from '../../components/layoutConfig/layout';
import { BsFileEarmarkText, BsClockHistory, BsPeople } from "react-icons/bs";
import LocalStorage from "../../helpers/localstorage";
import { setupAPIclient } from "../../services/setupAPIclient";
import { format, formatDistanceToNow } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

interface Doc {
  id: number;
  name: string | null;
  data_vencimento: string;
  caminho_doc: string;
  management_history: { id: number; data_vencimento: string; caminho_doc: string }[];
  management_signatories: { id: number; email: string }[];
  management_uploaddoc_reminder: { id: number; reminder_days: number }[];
  management_user_uploaddoc_access: {
    id: number;
    management_user: { id: number; name: string; email: string; number_phone: string };
  }[];
  management_document_access: {
    company_id?: number;
    region_id?: number;
    unit_id?: number;
    location_id?: number;
    management_company?: { id: number; name_company: string };
    management_region?: { id: number; name: string };
    management_unit?: { id: number; name: string };
    management_location?: { id: number; name: string };
  }[];
}

function HomePage() {
  const [docs, setDocs] = useState<Doc[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [popoverDate, setPopoverDate] = useState<string | null>(null);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const ApiClient = setupAPIclient(token);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [resDocs, resUsers] = await Promise.all([
          ApiClient.post(`/management/get/dashboard`),
          ApiClient.post(`/management/get/users`),
        ]);
        setDocs(resDocs.data.docs || []);
        setUsers(resUsers.data?.success || []);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setPopoverDate(null);
        setTargetEl(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- MÉTRICAS ---
  const totalContracts = docs.length;
  const totalUsers = users.length;

  const expiringContracts = docs.filter(d => {
    const reminderDays = d.management_uploaddoc_reminder?.[0]?.reminder_days ?? 0;
    const diffDays = (new Date(d.data_vencimento).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= reminderDays;
  }).length;

  const metrics = [
    { title: "Total de Documentos", value: totalContracts, icon: <BsFileEarmarkText size={40} /> },
    { title: "Para Vencer", value: expiringContracts, icon: <BsClockHistory size={40} /> },
    { title: "Total de Integrantes", value: totalUsers, icon: <BsPeople size={40} /> },
  ];

  // --- GRÁFICO ---
  const chartDataMap: Record<string, number> = {};
  docs.forEach(d => {
    d.management_document_access.forEach(access => {
      const company = access.management_company?.name_company ?? '—';
      const region = access.management_region?.name ?? '—';
      const unit = access.management_unit?.name ?? '—';
      const location = access.management_location?.name ?? '—';
      const key = `${company} / ${region} / ${unit} / ${location}`;
      chartDataMap[key] = (chartDataMap[key] || 0) + 1;
    });
  });

  const chartLabels = Object.keys(chartDataMap);
  const chartValues = Object.values(chartDataMap);
  const chartColors = chartLabels.map((_, idx) =>
    ['#5dade2', '#85c1e9', '#aed6f1', '#d6eaf8', '#2e86c1', '#2874a6'][idx % 6]
  );

  const chartData = {
    labels: chartLabels,
    datasets: [{
      label: 'Contratos por Hierarquia',
      data: chartValues,
      backgroundColor: chartColors
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      title: { display: true, text: 'Contratos por Company / Region / Unit / Location' },
      tooltip: {
        callbacks: {
          title: (context: any) => context[0]?.label ?? '',
        },
      },
    },
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 30,
          callback: function (value: any) {
            const label = chartLabels[value] || '';
            return label.length > 25 ? label.slice(0, 25) + '…' : label;
          },
        },
        title: { display: true, text: 'Hierarquia' },
      },
      y: {
        title: { display: true, text: 'Qtd' },
        beginAtZero: true,
        ticks: { precision: 0 },
      },
    },
  };

  const isSameDate = (d1: Date, d2: Date) =>
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate();

  const getContractsByDate = (date: Date) =>
    docs.filter(d => isSameDate(new Date(d.data_vencimento), date));

  const handlePopoverToggle = (date: Date, el: HTMLElement) => {
    const dateStr = date.toDateString();
    if (popoverDate === dateStr) {
      setPopoverDate(null);
      setTargetEl(null);
    } else {
      setPopoverDate(dateStr);
      setTargetEl(el);
    }
  };

  // --- RENDER ---
  return (
    <Layout>
      <Container fluid className="p-3" ref={containerRef}>
        <h4 className="mb-4 dashboard-title">Dashboard de Contratos</h4>

        {loading ? <p>Carregando...</p> : (
          <>
            <Row className="mb-4">
              {metrics.map((card, idx) => (
                <Col xs={12} sm={6} md={4} key={idx} className="mb-3">
                  <Card className="dashboard-card shadow-sm h-100">
                    <Card.Body className="d-flex align-items-center p-3">
                      <div className="card-icon me-3">{card.icon}</div>
                      <div className="card-info w-100">
                        <Card.Title className="card-title border-title">{card.title}</Card.Title>
                        <Card.Text className="card-value">{card.value}</Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <Row>
              <Col xs={12} md={8} className="mb-4">
                <Card className="shadow-sm h-100">
                  <Card.Body style={{ overflowX: 'auto' }}>
                    <div style={{
                      minWidth: `${chartLabels.length * 100}px`,
                      height: chartLabels.length > 15 ? "500px" : "400px"
                    }}>
                      <Bar data={chartData} options={chartOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} md={4} className="mb-4">
                <Card className="shadow-sm h-100">
                  <Card.Body>
                    <Calendar
                      locale="pt-BR"
                      formatDay={(locale, date) => format(date, 'dd', { locale: ptBR })}
                      formatMonthYear={(locale, date) => format(date, 'MMMM yyyy', { locale: ptBR })}
                      tileContent={({ date }) => {
                        const contracts = getContractsByDate(date);
                        return contracts.length > 0 ? (
                          <span
                            className="calendar-highlight"
                            onClick={(e) => handlePopoverToggle(date, e.currentTarget)}
                          >📌</span>
                        ) : null;
                      }}
                    />

                    <Overlay target={targetEl} show={Boolean(popoverDate)} placement="top">
                      <Popover id="popover-calendar" className="popover-custom">
                        <Popover.Header as="h3">
                          📌 {popoverDate && getContractsByDate(new Date(popoverDate)).length} contrato(s)
                        </Popover.Header>
                        <Popover.Body style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          {popoverDate && getContractsByDate(new Date(popoverDate)).map(c => (
                            <div key={c.id} className="popover-contract">
                              <p>📝 <strong>{c.name ?? "Sem nome"}</strong></p>
                              <p>📅 Vencimento: {
                                format(new Date(c.data_vencimento), "dd/MM/yyyy", 
                                { locale: ptBR })} 
                
                              </p>
                              <p>✉ Integrantes: {c.management_signatories.map(s => s.email).join(", ") || "—"}</p>
                              <p>👤 Usuários: {c.management_user_uploaddoc_access.map(u => u.management_user.name).join(", ") || "—"}</p>
                            </div>
                          ))}
                        </Popover.Body>
                      </Popover>
                    </Overlay>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        <style jsx>{`
          .dashboard-title { font-weight: 700; color: #2e4053; text-shadow: 1px 1px 2px #ddd; }
          .dashboard-card { border-radius: 16px; transition: all 0.4s ease; background-color: #ffffff; overflow: hidden; }
          .dashboard-card:hover { transform: translateY(-8px); box-shadow: 0 12px 25px rgba(0,0,0,0.15); }
          .card-icon { color: #5dade2; animation: bounce 1.2s infinite; min-width: 50px; text-align: center; }
          .card-title { font-size: 1.2rem; font-weight: 600; padding: 8px 12px; margin-bottom: 8px; border-radius: 8px; background-color: #ffffff; }
          .border-title { border-left: 6px solid rgb(255,36,0); padding-left: 10px; }
          .card-value { font-size: 2rem; font-weight: 700; color: #1b4f72; }
          @keyframes bounce { 0%,20%,50%,80%,100%{transform:translateY(0);}40%{transform:translateY(-8px);}60%{transform:translateY(-4px);} }
          .calendar-highlight { display:inline-block; font-size:14px; cursor:pointer; }
          .react-calendar { border-radius:12px; border:1px solid #ccc; box-shadow:0 6px 15px rgba(0,0,0,0.05); }
          .popover-custom { max-width: 360px; }
          .popover-custom .popover-header { font-weight: 700; color: #ff2400; text-align: center; }
          .popover-contract { border-left: 4px solid #5dade2; padding: 8px 12px; margin-bottom: 12px; border-radius: 6px; background-color: #fdfdfd; }
          .popover-contract ul { margin: 0; padding-left: 18px; }
        `}</style>
      </Container>
    </Layout>
  );
}

export default HomePage;
